import {
  inject,
  injectable,
  LazyServiceIdentifier,
  injectFromBase,
} from 'inversify'

import { BaseApiGateWayPlugins } from './BaseApiGateWayPlugins'
import { AbortRequestController, RequestBuilder, RequestTracker } from '../'
import { ApiClientStrategy } from '../Clients'
import { ApiGateWay } from '../GateWays/ApiGateWay'

@injectable()
@injectFromBase({ extendConstructorArguments: true })
export class BaseApiGateWay extends ApiGateWay {
  constructor(
    @inject(new LazyServiceIdentifier(() => RequestTracker))
    protected override readonly requestTracker: RequestTracker,
    @inject(new LazyServiceIdentifier(() => RequestBuilder))
    protected override readonly requestBuilder: RequestBuilder,
    @inject(new LazyServiceIdentifier(() => ApiClientStrategy))
    protected override readonly apiClientStrategy: ApiClientStrategy,
    @inject(new LazyServiceIdentifier(() => AbortRequestController))
    protected override readonly abortRequestController: AbortRequestController,

    @inject(new LazyServiceIdentifier(() => BaseApiGateWayPlugins))
    private readonly plugins: BaseApiGateWayPlugins,
  ) {
    super(
      requestTracker,
      requestBuilder,
      apiClientStrategy,
      abortRequestController,
    )

    return new Proxy<this>(this, <ProxyHandler<BaseApiGateWay>>{
      get: (target: BaseApiGateWay, prop: string, receiver: BaseApiGateWay) => {
        const member = Reflect.get(target, prop, receiver)

        if (typeof member !== 'function') {
          return member
        }

        return async (...args: unknown[]) => {
          return await this.resolveHandlersChain(
            prop,
            this.resolveSendersChain(member, target, args),
          )
        }
      },
    })
  }

  private resolveSendersChain(
    member: (...args: unknown[]) => Promise<unknown>,
    target: unknown,
    args: unknown[],
  ): () => Promise<unknown> {
    return this.plugins.senders.reduceRight(
      (next, sender) => {
        return () => sender.send(next)
      },
      () => member.call(target, ...args),
    )
  }

  private resolveHandlersChain(prop: string, sender: () => Promise<unknown>) {
    const chain = this.plugins.handlers.reduceRight((next, handler) => {
      return () => handler.handle(prop, next)
    }, sender)

    return chain()
  }
}
